import React, { createContext, ReactNode, TransitionEvent, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import classnames from "classnames";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import styles from "./Banner.module.scss";

/** Сколько длится уход карточки целиком: уезжает вбок, затем схлопывается её место ($leave + $collapse) */
export const BANNER_LEAVE_MS = 500;

const StackContext = createContext<HTMLElement | null>(null);

/**
 * Стопка карточек в правом верхнем углу полной версии — как уведомления-тостеры: карточки
 * встают одна под другой, закрытая уезжает, а нижние плавно поднимаются на её место.
 * Сами карточки (`Banner`) могут лежать где угодно в дереве — они попадают сюда порталом
 */
export const BannerStack = ({ children }: { children?: ReactNode }) => {
    const [host, setHost] = useState<HTMLElement | null>(null);

    return (
        <StackContext.Provider value={host}>
            {children}
            <div ref={setHost} className={styles.stack} />
        </StackContext.Provider>
    );
};

/**
 * Карточка стопки. Показ задаёт `open`: стала false — карточка уезжает анимацией и только
 * потом убирается со страницы. Крестик в правом верхнем углу — если передан `onClose`.
 * Вне стопки (например, в тестах компонента) рисуется на месте и без анимации
 */
export const Banner = ({ open, label, title, onClose, order = 0, className, children }: BannerProps) => {
    const host = useContext(StackContext);
    const [mounted, setMounted] = useState(open);
    const [leaving, setLeaving] = useState(false);
    const animated = !!host && !reducedMotion();

    useEffect(() => {
        if (open) {
            setMounted(true);
            setLeaving(false);
        } else if (mounted) {
            if (animated) {
                setLeaving(true);
            } else {
                setMounted(false);
            }
        }
    }, [open]);

    // конец анимации может не прийти (вкладка в фоне) — тогда карточку убирает таймер
    useEffect(() => {
        if (!leaving) {
            return;
        }

        const timer = window.setTimeout(() => setMounted(false), BANNER_LEAVE_MS + 100);

        return () => window.clearTimeout(timer);
    }, [leaving]);

    if (!mounted) {
        return null;
    }

    const onTransitionEnd = (e: TransitionEvent) => {
        if (leaving && e.target === e.currentTarget && e.propertyName === "grid-template-rows") {
            setMounted(false);
        }
    };

    const card = (
        <div
            className={classnames(
                styles.card,
                { [styles.animated]: animated, [styles.closable]: !!onClose },
                className
            )}
            role="region"
            aria-label={label}
        >
            {/* крестик — всегда в одной точке угла; заголовок или первая строка содержимого его обходит */}
            {onClose && <CloseButton onClose={onClose} />}
            {title && <div className={styles.title}>{title}</div>}
            {children}
        </div>
    );

    if (!host) {
        return card;
    }

    // место карточки в стопке: схлопывается после ухода, и нижние карточки поднимаются
    return createPortal(
        <div
            className={classnames(styles.slot, { [styles.leaving]: leaving })}
            style={{ order }}
            onTransitionEnd={onTransitionEnd}
        >
            <div className={styles.inner}>{card}</div>
        </div>,
        host
    );
};

const CloseButton = ({ onClose }: { onClose: () => void }) => (
    <button
        type="button"
        className={styles.close}
        aria-label="Закрыть"
        onClick={onClose}
    >
        <GeneralIcon type="general-cross-small" size={16} />
    </button>
);

interface BannerProps {
    /** Показывать карточку; false — уезжает анимацией */
    open: boolean;
    /** Подпись области для экранного диктора */
    label: string;
    /** Заголовок над содержимым */
    title?: ReactNode;
    /** Крестик в правом верхнем углу карточки */
    onClose?: () => void;
    /** Место в стопке: меньше — выше. Карточки с одинаковым порядком — в порядке появления */
    order?: number;
    className?: string;
    children?: ReactNode;
}

const reducedMotion = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
